import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Popover,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Paper,
  ClickAwayListener
} from '@mui/material';
import { useTranslation } from 'react-i18next';


const LOCAL_STORAGE_KEY_PREFIX = 'statusSelection_';

export default function StatusSelection({
  title,
  statuses,
  value,
  onChange,
  localStorageKeySuffix = '',
  defaultSelectedStatuses = [],
  maxWidth,
  style
}) {
  const { t } = useTranslation();
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const triggerRef = useRef(null);

  const localStorageKey = `${LOCAL_STORAGE_KEY_PREFIX}${localStorageKeySuffix}`;

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setSelectedStatuses(value);
      localStorage.setItem(localStorageKey, JSON.stringify(value));
    } else {
      const storedValue = localStorage.getItem(localStorageKey);
      if (storedValue) {
        try {
          setSelectedStatuses(JSON.parse(storedValue));
        } catch (e) {
          console.error("Failed to parse stored statuses from localStorage", e);
          setSelectedStatuses(defaultSelectedStatuses);
        }
      } else {
        setSelectedStatuses(defaultSelectedStatuses);
      }
    }
  }, [value, localStorageKey, defaultSelectedStatuses]);

  useEffect(() => {
    if (onChange && value === undefined) {
      onChange(selectedStatuses);
    }
    localStorage.setItem(localStorageKey, JSON.stringify(selectedStatuses));
  }, [selectedStatuses, onChange, value, localStorageKey]);


  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (event) => {
    const statusName = event.target.name;
    let newSelected;
    if (event.target.checked) {
      newSelected = [...selectedStatuses, statusName];
    } else {
      newSelected = selectedStatuses.filter((status) => status !== statusName);
    }
    setSelectedStatuses(newSelected);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'status-popover' : undefined;

  return (
    <>
      <Box
        ref={triggerRef}
        onClick={handleClick}
        style={style}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap', // Title and the status list should stay on one line initially
          minHeight: '20px',
          borderRadius: '4px',
          padding: '2px 4px',
          border: open ? '1px solid #ccc' : '1px solid transparent',
          '&:hover': {
            borderColor: '#999',
          },
          minWidth: '150px',
          maxWidth: maxWidth || 'fit-content'
        }}
      >
        <Typography variant="caption" sx={{ fontSize: '0.70rem', marginRight: 0.5, flexShrink: 0 }}>
          {t(title)}:
        </Typography>
        {/* Inner Box for statuses - now allows wrapping */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap', // <-- Changed to 'wrap' here!
            // Removed overflow, textOverflow, and whiteSpace as they are for truncation
            gap: 0.25,
            alignItems: 'center',
            flexGrow: 1 // Allow this box to grow and take available space
          }}
        >
          {selectedStatuses.length > 0 ? (
            selectedStatuses.map((status, index) => (
              <React.Fragment key={status}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                  {t(status)}
                </Typography>
                {index < selectedStatuses.length - 1 && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', marginRight: 0.25 }}>
                    ,
                  </Typography>
                )}
              </React.Fragment>
            ))
          ) : (
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
              {t('None selected')}
            </Typography>
          )}
        </Box>
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 200,
              overflowY: 'auto',
              minWidth: triggerRef.current ? triggerRef.current.offsetWidth : 'auto'
            },
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={3} sx={{ p: 1 }} onClick={(e) => e.stopPropagation()}>
            <FormGroup>
              {statuses.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={selectedStatuses.includes(status)}
                      onChange={handleStatusChange}
                      name={status}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {t(status)}
                    </Typography>
                  }
                  sx={{ '.MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                />
              ))}
            </FormGroup>
          </Paper>
        </ClickAwayListener>
      </Popover>
    </>
  );
}